import { createReducer, on } from '@ngrx/store';

import getFilterKey from '@main-application/administration/functions/get-filter-key';
import { UpdateStatus } from '@shared/enums/update-status';
import { UserType } from '@shared/enums/user-type';
import { getUserOrVendorList } from '@shared/functions/get-user-list.function';
import { GeneralRole } from '@shared/interfaces/general-role';
import { GeneralRoleToPermissionLevel } from '@shared/interfaces/general-role-to-permission-level';
import { PermissionLevel } from '@shared/interfaces/permission-level';
import { PropertyBasicInfo } from '@shared/interfaces/propertyBasicInfo';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { UserToGeneralRoleMapping } from '@shared/interfaces/user-to-general-role-mapping';
import { UserToPropertyMapping } from '@shared/interfaces/user-to-property-mapping';
import { UserWithGeneralRole } from '@shared/interfaces/user-with-general-role';
import { RestUserModel, UserFilter } from '@shared/interfaces/user.interface';
import { SummaryStatsGroup } from '@ui-components/components/summary-expandable-table/summary-expandable-table.model';

import * as AdministrationActions from '../actions/administration.actions';
import { setUserListFilter } from '../actions/administration.actions';

export const administrationFeatureKey = 'administration';

export interface IAdministrationStateTree {
  readonly [administrationFeatureKey]: State;
}

export interface State {
  portfolioActiveUsers: RestUserModel[];
  portfolioInactiveUsers: RestUserModel[];
  portfolioUsersLoading: boolean;

  activeUsers: RestUserModel[];
  activeUserOrVendorOptionsList: IRadioButtonOption<number>[];
  inactiveUsers: RestUserModel[];
  cachedUsers: { [filterKey: string]: RestUserModel[] };

  usersLoading: boolean;
  activePropertyUsers: RestUserModel[];
  propertyUsersLoading: boolean;
  permissionLevels: PermissionLevel[];
  generalRolesToPermissionMapping: GeneralRoleToPermissionLevel[];
  propertiesRoles: any;
  userToGeneralRole: UserToGeneralRoleMapping[];
  userToGeneralRoleByUserId: Record<number, UserToGeneralRoleMapping[]>;
  userToGeneralRoleLoadingByUserId: Record<number, boolean>;
  selectedUsers: UserWithGeneralRole[];
  generalRoles: GeneralRole[];
  usersWithGeneralRoles: UserWithGeneralRole[];
  usersWithGeneralRolesLoading: boolean;
  propertiesStructure: PropertyBasicInfo[];

  addNewUserInProgress: boolean;
  addedUser: RestUserModel;
  deleteGeneralRoleInProgress: boolean;
  selectedGeneralRole: GeneralRole;
  saveGeneralRoleInProgress: boolean;
  userToPropertyMapping: UserToPropertyMapping[];
  userToPropertyMappingByUserId: Record<number, UserToPropertyMapping[]>;
  userToPropertyMappingLoadingByUserId: Record<number, boolean>;
  generalRoleRadioList: IRadioButtonOption<number>[];

  userUpdateInProgress: boolean;
  userUpdateStatus: UpdateStatus;

  userInviteInProgress: boolean;
  userInviteStatus: UpdateStatus;
  userInvited: RestUserModel | null;

  saveUserToGeneralRoleInProgress: boolean;
  saveUserToGeneralRoleStatus: UpdateStatus;

  assignUserIntoPropertyInProgress: boolean;
  assignUserIntoPropertyStatus: UpdateStatus;

  removingPropertyAssignedUserInProgress: boolean;
  removingPropertyAssignedUserStatus: UpdateStatus;
  removeUserInProgress: boolean;
  userListFilter: Partial<UserFilter>;

  usersByPropertyIds: RestUserModel[];
  usersByPropertyIdsLoading: boolean;
  usersByPropertyIdsLoadedKey: string | null;
  usersByPropertyIdsLoadedUserTypes: number[];
  usersByPropertyIdsPendingKey: string | null;
  usersByPropertyIdsPendingUserTypes: number[];
  usersByPropertyIdsNone: RestUserModel[];
  usersByPropertyIdsNoneLoading: boolean;
  usersByPropertyIdsEmployee: RestUserModel[];
  usersByPropertyIdsEmployeeLoading: boolean;
  usersByPropertyIdsContractor: RestUserModel[];
  usersByPropertyIdsContractorLoading: boolean;
  usersByPropertyIdsResident: RestUserModel[];
  usersByPropertyIdsResidentLoading: boolean;
  userSummaryStats: SummaryStatsGroup[] | null;
  userSummaryStatsSecondary: SummaryStatsGroup[] | null;
  userSummaryLoading: boolean;
  userSummaryError: any;
  userSummaryStatsSecondaryLoading: boolean;
  userSummaryStatsSecondaryError: any;
}

export const initialState: State = {
  portfolioActiveUsers: [],
  portfolioInactiveUsers: [],
  portfolioUsersLoading: false,

  addNewUserInProgress: false,
  addedUser: null,
  deleteGeneralRoleInProgress: false,
  generalRoles: [],
  generalRolesToPermissionMapping: [],
  permissionLevels: [],
  propertiesRoles: null,
  propertiesStructure: [],
  activePropertyUsers: [],
  propertyUsersLoading: false,
  saveGeneralRoleInProgress: false,

  selectedGeneralRole: null,
  selectedUsers: [],
  userToGeneralRole: [],
  userToGeneralRoleByUserId: {},
  userToGeneralRoleLoadingByUserId: {},
  userToPropertyMapping: [],
  userToPropertyMappingByUserId: {},
  userToPropertyMappingLoadingByUserId: {},
  activeUsers: [],
  activeUserOrVendorOptionsList: [],
  inactiveUsers: [],
  cachedUsers: {},
  usersLoading: false,
  usersWithGeneralRoles: [],
  usersWithGeneralRolesLoading: false,

  generalRoleRadioList: [],

  userUpdateInProgress: false,
  userUpdateStatus: null,

  userInviteInProgress: false,
  userInviteStatus: null,
  userInvited: null,

  saveUserToGeneralRoleInProgress: false,
  saveUserToGeneralRoleStatus: null,

  assignUserIntoPropertyInProgress: false,
  assignUserIntoPropertyStatus: null,

  removingPropertyAssignedUserInProgress: false,
  removingPropertyAssignedUserStatus: null,
  removeUserInProgress: false,
  userListFilter: {},

  usersByPropertyIds: [],
  usersByPropertyIdsLoading: false,
  usersByPropertyIdsLoadedKey: null,
  usersByPropertyIdsLoadedUserTypes: [],
  usersByPropertyIdsPendingKey: null,
  usersByPropertyIdsPendingUserTypes: [],
  usersByPropertyIdsNone: [],
  usersByPropertyIdsNoneLoading: false,
  usersByPropertyIdsEmployee: [],
  usersByPropertyIdsEmployeeLoading: false,
  usersByPropertyIdsContractor: [],
  usersByPropertyIdsContractorLoading: false,
  usersByPropertyIdsResident: [],
  usersByPropertyIdsResidentLoading: false,
  userSummaryStats: null,
  userSummaryStatsSecondary: null,
  userSummaryLoading: false,
  userSummaryError: null,
  userSummaryStatsSecondaryLoading: false,
  userSummaryStatsSecondaryError: null,
};

export const reducer = createReducer(
  initialState,

  on(AdministrationActions.loadAllUsers, (state, action) => ({
    ...state,
    usersLoading: true,
    usersWithGeneralRolesLoading: true,
  })),
  on(AdministrationActions.loadAllUsersSucceeded, (state, action) => {
    const users = [...action.activeUsers, ...action.inactiveUsers];

    return {
      ...state,
      activeUsers: action.activeUsers,
      activeUserOrVendorOptionsList: getUserOrVendorList(action.activeUsers),
      inactiveUsers: action.inactiveUsers,
      usersLoading: false,
      cachedUsers: {
        ...state.cachedUsers,
        [getFilterKey(action.filter)]: users,
      },
    };
  }),
  on(AdministrationActions.loadPortfolioUsers, (state, action) => ({
    ...state,
    portfolioUsersLoading: true,
  })),
  on(AdministrationActions.loadPortfolioUsersSucceeded, (state, action) => ({
    ...state,
    portfolioActiveUsers: action.activeUsers,
    portfolioInactiveUsers: action.inactiveUsers,
    portfolioUsersLoading: false,
  })),
  on(AdministrationActions.loadAllUsersFailed, (state, action) => ({ ...state, users: [], usersLoading: false })),
  on(AdministrationActions.clearAllUsers, (state, action) => ({ ...state, users: null })),

  on(AdministrationActions.loadPropertyUsers, (state, action) => ({
    ...state,
    activePropertyUsers: [],
    propertyUsersLoading: true,
  })),
  on(AdministrationActions.loadPropertyUsersSucceeded, (state, action) => ({
    ...state,
    activePropertyUsers: action.activePropertyUsers,
    propertyUsersLoading: false,
  })),
  on(AdministrationActions.loadPropertyUsersFailed, (state, action) => ({
    ...state,
    activePropertyUsers: [],
    propertyUsersLoading: false,
  })),

  on(AdministrationActions.loadAllPermissionLevelsSucceeded, (state, action) => ({
    ...state,
    permissionLevels: action.result,
  })),
  on(AdministrationActions.loadAllGeneralRolesToPermissionMappingSucceeded, (state, action) => ({
    ...state,
    generalRolesToPermissionMapping: action.result,
  })),
  on(AdministrationActions.loadAllPropertiesRolesSucceeded, (state, action) => ({
    ...state,
    propertiesRoles: action.result,
  })),
  on(AdministrationActions.clearAllPropertiesRoles, (state, action) => ({ ...state, propertiesRoles: undefined })),
  on(AdministrationActions.loadUserToRoleMappingSucceeded, (state, action) => ({
    ...state,
    userToGeneralRole: action.userToGeneralRole,
  })),
  on(AdministrationActions.loadUserToRoleMappingByUserId, (state, action) => ({
    ...state,
    userToGeneralRoleLoadingByUserId: {
      ...state.userToGeneralRoleLoadingByUserId,
      [action.userId]: true,
    },
  })),
  on(AdministrationActions.loadUserToRoleMappingByUserIdSucceeded, (state, action) => ({
    ...state,
    userToGeneralRoleByUserId: {
      ...state.userToGeneralRoleByUserId,
      [action.userId]: action.userToGeneralRole,
    },
    userToGeneralRoleLoadingByUserId: {
      ...state.userToGeneralRoleLoadingByUserId,
      [action.userId]: false,
    },
    userToGeneralRole: state.userToGeneralRole.map(mapping =>
      mapping.userId === action.userId ? action.userToGeneralRole[0] || mapping : mapping
    ),
  })),
  on(AdministrationActions.loadUserToRoleMappingByUserIdError, (state, action) => ({
    ...state,
    userToGeneralRoleLoadingByUserId: {
      ...state.userToGeneralRoleLoadingByUserId,
      [action.userId]: false,
    },
  })),
  on(AdministrationActions.setSelectedUsers, (state, action) => ({ ...state, selectedUsers: action.users })),
  on(AdministrationActions.loadAllGeneralRolesSucceeded, (state, action) => ({
    ...state,
    generalRoles: action.generalRoles,
  })),
  on(AdministrationActions.clearAllGeneralRoles, (state, action) => ({ ...state, generalRoles: undefined })),
  on(AdministrationActions.setAllUsersCombinedWithGeneralRoles, (state, action) => ({
    ...state,
    usersWithGeneralRoles: action.usersWithGeneralRoles,
    usersWithGeneralRolesLoading: false,
  })),
  on(AdministrationActions.clearPropertiesStructure, (state, action) => ({ ...state, propertiesStructure: undefined })),
  on(AdministrationActions.loadPropertiesStructureSucceeded, (state, action) => ({
    ...state,
    propertiesStructure: action.propertiesStructure,
  })),
  on(AdministrationActions.saveUsersRolesAndUnits, (state, action) => ({
    ...state,
    saveUserToGeneralRoleInProgress: true,
    saveUserToPropertyInProgress: true,
  })),
  on(setUserListFilter, (state, action) => ({
    ...state,
    userListFilter: action,
  })),

  /**
   * assign user into property
   */
  on(AdministrationActions.assignUserToProperty, (state, action) => ({
    ...state,
    assignUserIntoPropertyInProgress: true,
    assignUserIntoPropertyStatus: UpdateStatus.IN_PROGRESS,
  })),
  on(AdministrationActions.assignUserToPropertySuccess, (state, action) => ({
    ...state,
    assignUserIntoPropertyInProgress: false,
    assignUserIntoPropertyStatus: UpdateStatus.UPDATED,
  })),
  on(AdministrationActions.assignUserToPropertyError, (state, action) => ({
    ...state,
    assignUserIntoPropertyInProgress: false,
    assignUserIntoPropertyStatus: UpdateStatus.ERROR,
  })),

  /**
   * removing property assigned user
   */
  on(AdministrationActions.removingPropertyAssignedUser, (state, action) => ({
    ...state,
    removingPropertyAssignedUserInProgress: true,
    removingPropertyAssignedUserStatus: UpdateStatus.IN_PROGRESS,
  })),
  on(
    AdministrationActions.removingPropertyAssignedUserSuccess,
    AdministrationActions.removingPropertyAssignedUserError,
    (state, action) => ({
      ...state,
      removingPropertyAssignedUserInProgress: false,
      removingPropertyAssignedUserStatus: UpdateStatus.DELETED,
    })
  ),
  on(
    AdministrationActions.removingPropertyAssignedUserSuccess,
    AdministrationActions.removingPropertyAssignedUserError,
    (state, action) => ({
      ...state,
      removingPropertyAssignedUserInProgress: false,
      removingPropertyAssignedUserStatus: UpdateStatus.ERROR,
    })
  ),

  /**
   * save User To General Role
   */
  on(AdministrationActions.saveUserToGeneralRole, (state, action) => ({
    ...state,
    saveUserToGeneralRoleInProgress: true,
    saveUserToGeneralRoleStatus: UpdateStatus.IN_PROGRESS,
  })),

  on(AdministrationActions.saveUserToGeneralRoleSuccess, (state, action) => ({
    ...state,
    saveUserToGeneralRoleInProgress: false,
    saveUserToGeneralRoleStatus: UpdateStatus.UPDATED,
  })),

  on(AdministrationActions.saveUserToGeneralRoleError, (state, action) => ({
    ...state,
    saveUserToGeneralRoleInProgress: false,
    saveUserToGeneralRoleStatus: UpdateStatus.ERROR,
  })),

  on(AdministrationActions.clearUserToRoleMapping, (state, action) => ({
    ...state,
    saveUserToGeneralRoleInProgress: undefined,
  })),

  on(
    AdministrationActions.assignUserToPropertySuccess,
    AdministrationActions.assignUserToPropertyError,
    (state, action) => ({ ...state, saveUserToPropertyInProgress: false })
  ),
  on(AdministrationActions.startAddNewUser, (state, action) => ({
    ...state,
    addNewUserInProgress: true,
    addedUser: null,
  })),

  on(AdministrationActions.addingNewUserSucceeded, (state, action) => ({
    ...state,
    addNewUserInProgress: false,
    addedUser: action.user,
  })),

  on(AdministrationActions.addingNewUserFailed, (state, action) => ({
    ...state,
    addNewUserInProgress: false,
    addedUser: null,
  })),
  on(AdministrationActions.clearAddedUser, (state, action) => ({
    ...state,
    addedUser: null,
  })),

  on(AdministrationActions.startDeleteGeneralRole, (state, action) => ({
    ...state,
    deleteGeneralRoleInProgress: true,
  })),
  on(
    AdministrationActions.deleteGeneralRoleSucceeded,
    AdministrationActions.deleteGeneralRoleFailed,
    (state, action) => ({ ...state, deleteGeneralRoleInProgress: false })
  ),
  on(AdministrationActions.selectGeneralRoleToEdit, (state, action) => ({
    ...state,
    selectedGeneralRole: action.generalRole,
  })),
  on(AdministrationActions.clearSelectedGeneralRole, (state, action) => ({ ...state, selectedGeneralRole: undefined })),
  on(AdministrationActions.startSavingGeneralRole, (state, action) => ({ ...state, saveGeneralRoleInProgress: true })),
  on(
    AdministrationActions.savingGeneralRoleSucceeded,
    AdministrationActions.savingGeneralRoleFailed,
    (state, action) => ({ ...state, saveGeneralRoleInProgress: false })
  ),
  on(AdministrationActions.loadAllUserToProperyMappingsSucceeded, (state, action) => ({
    ...state,
    userToPropertyMapping: action.userToPropertyMapping,
  })),
  on(AdministrationActions.loadUserToPropertyMappingsByUserId, (state, action) => ({
    ...state,
    userToPropertyMappingLoadingByUserId: {
      ...state.userToPropertyMappingLoadingByUserId,
      [action.userId]: true,
    },
  })),
  on(AdministrationActions.loadUserToPropertyMappingsByUserIdSucceeded, (state, action) => ({
    ...state,
    userToPropertyMappingByUserId: {
      ...state.userToPropertyMappingByUserId,
      [action.userId]: action.userToPropertyMapping,
    },
    userToPropertyMappingLoadingByUserId: {
      ...state.userToPropertyMappingLoadingByUserId,
      [action.userId]: false,
    },
  })),
  on(AdministrationActions.loadUserToPropertyMappingsByUserIdError, (state, action) => ({
    ...state,
    userToPropertyMappingLoadingByUserId: {
      ...state.userToPropertyMappingLoadingByUserId,
      [action.userId]: false,
    },
  })),
  on(AdministrationActions.clearAllUserToPropertyMappings, (state, action) => ({
    ...state,
    userToPropertyMapping: undefined,
  })),
  on(AdministrationActions.setGeneralRoleRadioList, (state, action) => ({
    ...state,
    generalRoleRadioList: action.generalRoleRadioList,
  })),

  /**
   * invite User
   */
  on(AdministrationActions.inviteNewUser, AdministrationActions.inviteNewResidentUser, (state, action) => {
    return {
      ...state,
      userInviteInProgress: true,
      userInviteStatus: UpdateStatus.IN_PROGRESS,
      userInvited: null,
    };
  }),
  on(
    AdministrationActions.inviteNewUserSuccess,
    AdministrationActions.inviteNewResidentUserSuccess,
    (state, action) => ({
      ...state,
      userInviteInProgress: false,
      userInviteStatus: UpdateStatus.OK,
      userInvited: action.user,
    })
  ),
  on(AdministrationActions.inviteNewUserError, AdministrationActions.inviteNewResidentUserError, (state, action) => ({
    ...state,
    userInviteInProgress: false,
    userInviteStatus: UpdateStatus.ERROR,
    userInvited: null,
  })),

  /**
   * update User
   */
  on(AdministrationActions.updateUserData, AdministrationActions.updateResidentUserData, (state, action) => ({
    ...state,
    userUpdateInProgress: true,
    userUpdateStatus: UpdateStatus.IN_PROGRESS,
  })),
  on(
    AdministrationActions.updateUserDataSuccess,
    AdministrationActions.updateResidentUserDataSuccess,
    (state, action) => ({
      ...state,
      userUpdateInProgress: false,
      userUpdateStatus: UpdateStatus.UPDATED,
    })
  ),
  on(
    AdministrationActions.updateUserDataError,
    AdministrationActions.updateResidentUserDataError,
    AdministrationActions.savingGeneralRoleFailed,
    (state, action) => ({
      ...state,
      userUpdateInProgress: false,
      userUpdateStatus: UpdateStatus.ERROR,
    })
  ),
  on(AdministrationActions.removeUser, (state, action) => ({
    ...state,
    removeUserInProgress: true,
  })),
  on(AdministrationActions.removeUserFailed, AdministrationActions.removeUserSucceeded, (state, action) => ({
    ...state,
    removeUserInProgress: false,
  })),

  /**
   * clear User Preview Data
   **/
  on(AdministrationActions.clearUserPreviewData, (state, action) => ({
    ...state,
    userUpdateInProgress: false,
    userUpdateStatus: null,
    userInviteInProgress: false,
    userInviteStatus: null,
    userInvited: null,
    saveUserToGeneralRoleInProgress: false,
    saveUserToGeneralRoleStatus: null,
    assignUserIntoPropertyInProgress: false,
    assignUserIntoPropertyStatus: null,
    removingPropertyAssignedUserInProgress: false,
    removingPropertyAssignedUserStatus: null,
  })),

  on(AdministrationActions.loadUsersByPropertyIds, (state, { propertyIds, userTypes }) => {
    // If no property IDs, the effect will filter it out, so don't set loading to true
    // Set loading to false and users to empty array immediately
    if (!propertyIds || propertyIds.length === 0) {
      return {
        ...state,
        usersByPropertyIds: [],
        usersByPropertyIdsLoading: false,
        usersByPropertyIdsLoadedKey: null,
        usersByPropertyIdsLoadedUserTypes: [],
        usersByPropertyIdsPendingKey: null,
        usersByPropertyIdsPendingUserTypes: [],
      };
    }

    const sortedPropertyIds = [...propertyIds].sort((a, b) => a - b);
    const propertyIdsKey = sortedPropertyIds.join(',');

    const isSameAsLoaded = state.usersByPropertyIdsLoadedKey === propertyIdsKey;
    const isSameAsPending = state.usersByPropertyIdsPendingKey === propertyIdsKey;
    const isSamePropertyIds = isSameAsLoaded || isSameAsPending;

    const requestedTypes = userTypes || [UserType.None, UserType.Employee, UserType.Contractor];
    const newPendingTypes = isSamePropertyIds
      ? [...new Set([...state.usersByPropertyIdsPendingUserTypes, ...requestedTypes])]
      : requestedTypes;

    return {
      ...state,
      usersByPropertyIds: isSameAsLoaded ? state.usersByPropertyIds : [],
      usersByPropertyIdsLoading: true,
      usersByPropertyIdsLoadedKey: isSameAsLoaded ? state.usersByPropertyIdsLoadedKey : null,
      usersByPropertyIdsLoadedUserTypes: isSameAsLoaded ? state.usersByPropertyIdsLoadedUserTypes : [],
      usersByPropertyIdsPendingKey: propertyIdsKey,
      usersByPropertyIdsPendingUserTypes: newPendingTypes,
    };
  }),
  on(
    AdministrationActions.loadUsersByPropertyIdsSucceeded,
    (state, { usersByPropertyIds, propertyIdsKey, loadedUserTypes, shouldMerge, isNoOp }) => {
      if (isNoOp) {
        return {
          ...state,
          usersByPropertyIdsLoading: false,
          usersByPropertyIdsPendingKey: null,
          usersByPropertyIdsPendingUserTypes: [],
        };
      }

      let finalUsers: RestUserModel[];
      let finalLoadedTypes: number[];

      if (shouldMerge && state.usersByPropertyIdsLoadedKey === propertyIdsKey) {
        const existingUserIds = new Set(state.usersByPropertyIds.map(u => u.id));
        const newUniqueUsers = usersByPropertyIds.filter(u => !existingUserIds.has(u.id));
        finalUsers = [...state.usersByPropertyIds, ...newUniqueUsers];
        finalLoadedTypes = [...new Set([...state.usersByPropertyIdsLoadedUserTypes, ...loadedUserTypes])];
      } else {
        finalUsers = usersByPropertyIds;
        finalLoadedTypes = loadedUserTypes;
      }

      return {
        ...state,
        usersByPropertyIds: finalUsers,
        usersByPropertyIdsLoading: false,
        usersByPropertyIdsLoadedKey: propertyIdsKey,
        usersByPropertyIdsLoadedUserTypes: finalLoadedTypes,
        usersByPropertyIdsPendingKey: null,
        usersByPropertyIdsPendingUserTypes: [],
      };
    }
  ),
  on(AdministrationActions.loadUsersByPropertyIdsFailed, state => ({
    ...state,
    usersByPropertyIdsLoading: false,
    usersByPropertyIdsPendingKey: null,
    usersByPropertyIdsPendingUserTypes: [],
  })),
  on(AdministrationActions.loadUsersByPropertyIdsNone, state => ({
    ...state,
    usersByPropertyIdsNone: [],
    usersByPropertyIdsNoneLoading: true,
  })),
  on(AdministrationActions.loadUsersByPropertyIdsNoneSucceeded, (state, { usersByPropertyIdsNone }) => ({
    ...state,
    usersByPropertyIdsNone,
    usersByPropertyIdsNoneLoading: false,
  })),
  on(AdministrationActions.loadUsersByPropertyIdsNoneFailed, state => ({
    ...state,
    usersByPropertyIdsNone: [],
    usersByPropertyIdsNoneLoading: false,
  })),
  on(AdministrationActions.loadUsersByPropertyIdsEmployee, state => ({
    ...state,
    usersByPropertyIdsEmployee: [],
    usersByPropertyIdsEmployeeLoading: true,
  })),
  on(AdministrationActions.loadUsersByPropertyIdsEmployeeSucceeded, (state, { usersByPropertyIdsEmployee }) => ({
    ...state,
    usersByPropertyIdsEmployee,
    usersByPropertyIdsEmployeeLoading: false,
  })),
  on(AdministrationActions.loadUsersByPropertyIdsEmployeeFailed, state => ({
    ...state,
    usersByPropertyIdsEmployee: [],
    usersByPropertyIdsEmployeeLoading: false,
  })),
  on(AdministrationActions.loadUsersByPropertyIdsContractor, state => ({
    ...state,
    usersByPropertyIdsContractor: [],
    usersByPropertyIdsContractorLoading: true,
  })),
  on(AdministrationActions.loadUsersByPropertyIdsContractorSucceeded, (state, { usersByPropertyIdsContractor }) => ({
    ...state,
    usersByPropertyIdsContractor,
    usersByPropertyIdsContractorLoading: false,
  })),
  on(AdministrationActions.loadUsersByPropertyIdsContractorFailed, state => ({
    ...state,
    usersByPropertyIdsContractor: [],
    usersByPropertyIdsContractorLoading: false,
  })),
  on(AdministrationActions.loadUsersByPropertyIdsResident, state => ({
    ...state,
    usersByPropertyIdsResident: [],
    usersByPropertyIdsResidentLoading: true,
  })),
  on(AdministrationActions.loadUsersByPropertyIdsResidentSucceeded, (state, { usersByPropertyIdsResident }) => ({
    ...state,
    usersByPropertyIdsResident,
    usersByPropertyIdsResidentLoading: false,
  })),
  on(AdministrationActions.loadUsersByPropertyIdsResidentFailed, state => ({
    ...state,
    usersByPropertyIdsResident: [],
    usersByPropertyIdsResidentLoading: false,
  })),
  on(AdministrationActions.loadUserSummaryStatsV2, state => ({
    ...state,
    userSummaryLoading: true,
    userSummaryError: null,
  })),
  on(AdministrationActions.loadUserSummaryStatsV2Success, (state, { stats }) => ({
    ...state,
    userSummaryStats: stats,
    userSummaryLoading: false,
  })),
  on(AdministrationActions.loadUserSummaryStatsV2Failed, (state, { error }) => ({
    ...state,
    userSummaryError: error,
    userSummaryLoading: false,
  })),
  on(AdministrationActions.loadUserSummaryStatsV2Secondary, state => ({
    ...state,
    userSummaryStatsSecondaryLoading: true,
    userSummaryStatsSecondaryError: null,
  })),
  on(AdministrationActions.loadUserSummaryStatsV2SecondarySuccess, (state, { stats }) => ({
    ...state,
    userSummaryStatsSecondary: stats,
    userSummaryStatsSecondaryLoading: false,
  })),
  on(AdministrationActions.loadUserSummaryStatsV2SecondaryFailed, (state, { error }) => ({
    ...state,
    userSummaryStatsSecondaryError: error,
    userSummaryStatsSecondaryLoading: false,
  }))
);
